"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { MapPin, Calendar, Clock, Car, MapPinned } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useState, useEffect } from "react";
const regions = [{
  id: 1,
  name: "Connecticut",
  description: "Home base for premium car service",
  cities: "Hartford, New Haven, Stamford, Bridgeport, Waterbury, Norwalk, Danbury, Greenwich, Fairfield County",
  coverage: "Statewide coverage with immediate availability",
  x: "25%",
  y: "35%"
}, {
  id: 2,
  name: "New York City",
  description: "Complete NYC metro area coverage",
  cities: "Manhattan, Brooklyn, Queens, Bronx, Staten Island, Financial District, Midtown, Upper East & West Side",
  coverage: "24/7 service to all five boroughs",
  x: "45%",
  y: "40%"
}, {
  id: 3,
  name: "Long Island",
  description: "Comprehensive Long Island service",
  cities: "Nassau County, Suffolk County, Hamptons, North Fork, South Shore, Gold Coast, Jones Beach area",
  coverage: "Express routes to all major destinations",
  x: "65%",
  y: "45%"
}, {
  id: 4,
  name: "New Jersey",
  description: "Northern New Jersey coverage",
  cities: "Newark, Jersey City, Hoboken, Morristown, Paterson, Elizabeth, Bergen County, Essex County",
  coverage: "Fast connections to NYC and all major airports",
  x: "35%",
  y: "60%"
}];
const vehicleTypes = [{
  id: "sedan",
  name: "Luxury Sedan"
}, {
  id: "suv",
  name: "SUV"
}, {
  id: "luxury-suv",
  name: "Luxury SUV"
}];
export default function OurPrimaryServiceAreas() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.15
  });
  const [formData, setFormData] = useState({
    pickupLocation: "",
    dropoffLocation: "",
    date: "",
    time: "",
    vehicleType: ""
  });
  useEffect(() => {
    const script = document.createElement("script");
    script.type = "text/javascript";
    script.src = "https://book.mylimobiz.com/v4/widgets/widget-loader.js";
    script.async = true;
    document.body.appendChild(script);
    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log("Booking data:", formData);
  };
  return <section ref={sectionRef} className="relative w-full overflow-hidden bg-background py-24 md:py-32">
      {}
      <div className="pointer-events-none absolute inset-0 opacity-20" style={{
      backgroundImage: "url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1431253/img/service_area_map_silhouette.png)",
      backgroundSize: "cover",
      backgroundPosition: "center",
      transform: isVisible ? "translateY(0)" : "translateY(-5%)",
      transition: "transform 800ms cubic-bezier(0.25, 0.46, 0.45, 0.94)"
    }} />

      {}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-background/80 via-background/60 to-background/80" />

      {}
      <div className="container relative z-10 mx-auto px-4 md:px-6">
        {}
        <div className="mx-auto mb-16 max-w-3xl text-center">
          <h2 className="mb-4 font-display text-3xl font-bold text-foreground md:text-4xl lg:text-5xl">
            Our Primary Service Areas
          </h2>
          <p className="text-base text-muted md:text-lg">
            Serving the tri-state area with reliable, luxury transportation across Connecticut, New York, and New Jersey
          </p>
        </div>

        {}
        <div className="mx-auto mb-16 max-w-4xl">
          {}
        </div>

        {}
        <div className="relative mx-auto aspect-[4/3] max-w-5xl md:aspect-[16/9]">
          {regions.map((region, i) => <div key={region.id} data-index={i} className="group absolute" style={{
          left: region.x,
          top: region.y,
          transform: "translate(-50%, -50%)",
          opacity: isVisible ? 1 : 0,
          animation: isVisible ? `fadeInUp 600ms cubic-bezier(0.25, 0.46, 0.45, 0.94) ${i * 150 + 400}ms both` : "none"
        }}>
              {}
              <div className="relative flex flex-col items-center">
                <div className="flex size-16 items-center justify-center rounded-full bg-accent text-foreground-accent shadow-medium transition-all duration-[var(--duration-medium)] ease-[var(--ease-out-back)] hover:-translate-y-1.5 hover:shadow-strong hover:bg-accent-hover md:size-20" style={{
              boxShadow: "0 4px 16px rgb(0 0 0 / 0.12), 0 2px 6px rgb(0 0 0 / 0.08)"
            }}>
                  <MapPin className="size-8 md:size-10" strokeWidth={2} />
                </div>

                {}
                <div className="pointer-events-none absolute top-full mt-3 w-72 translate-y-2 opacity-0 transition-all duration-[var(--duration-medium)] ease-[var(--ease-out-back)] group-hover:translate-y-0 group-hover:opacity-100 md:w-80">
                  <div className="glass-effect px-5 py-4 text-left">
                    <p className="mb-2 font-display text-base font-semibold text-foreground md:text-lg">
                      {region.name}
                    </p>
                    <p className="mb-3 text-xs text-muted md:text-sm">
                      {region.description}
                    </p>
                    <div className="mb-3 border-t border-muted/20 pt-3">
                      <p className="mb-1 text-xs font-medium text-foreground md:text-sm">
                        Areas Covered:
                      </p>
                      <p className="text-xs leading-relaxed text-muted md:text-sm">
                        {region.cities}
                      </p>
                    </div>
                    <div className="border-t border-muted/20 pt-3">
                      <p className="text-xs font-medium text-accent md:text-sm">
                        {region.coverage}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>)}
        </div>

        {}
        <div className="mt-16 flex justify-center">
          <div className="card bg-secondary px-4 py-6 w-full max-w-6xl" style={{
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? "translateY(0)" : "translateY(2rem)",
          transition: "opacity 600ms cubic-bezier(0.25, 0.46, 0.45, 0.94) 600ms, transform 600ms cubic-bezier(0.25, 0.46, 0.45, 0.94) 600ms"
        }}>
            <Link to="https://book.mylimobiz.com/v4/CSC" data-ores-widget="website" data-ores-alias="CSC" className="inline-flex items-center justify-center gap-2 rounded-lg bg-accent px-6 py-3 font-display text-base font-semibold text-foreground-accent shadow-medium transition-all duration-[var(--duration-medium)] ease-[var(--ease-spring)] hover:-translate-y-0.5 hover:bg-accent-hover hover:shadow-strong active:translate-y-0 active:bg-accent-active md:text-lg">
              Online Reservations
            </Link>
          </div>
        </div>

        {}
        <div className="mt-12 text-center">
          <p className="text-sm text-muted md:text-base">Available 24/7 with Immediate Booking Confirmation • Professional Chauffeurs • On-Time Guarantee</p>
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translate(-50%, calc(-50% + 1rem));
          }
          to {
            opacity: 1;
            transform: translate(-50%, -50%);
          }
        }
      `}</style>
    </section>;
}